/**
 * Hook onboarding d'activation — statut dérivé de la donnée réelle + actions (dismiss,
 * marquage forecast vu, coach marks). Le PATCH renvoie le statut frais : on le pousse
 * directement dans le cache pour un cochage immédiat.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { onboardingApi, type OnboardingAction } from '@/services/api';
import type { OnboardingStatus } from '@/types';
import { useMutationWithToast } from './useMutationWithToast';

export const onboardingKeys = {
    all: ['onboarding'] as const,
    status: () => [...onboardingKeys.all, 'status'] as const,
};

export function useOnboardingStatus() {
    return useQuery({
        queryKey: onboardingKeys.status(),
        queryFn: () => onboardingApi.getStatus(),
        staleTime: 30_000,
    });
}

/**
 * Mutation onboarding (silencieuse en succès : pas de toast, seul l'échec en affiche un).
 */
export function useOnboardingPatch() {
    const queryClient = useQueryClient();

    return useMutationWithToast<OnboardingStatus, Error, OnboardingAction>({
        mutationFn: (payload: OnboardingAction) => onboardingApi.patch(payload),
        operationName: 'Onboarding',
        onSuccess: (status) => {
            queryClient.setQueryData(onboardingKeys.status(), status);
        },
    });
}

/**
 * Entrée / sortie du mode exemple isolé. L'org effective change côté serveur :
 * on vide TOUT le cache de requêtes puis on pousse le statut frais renvoyé,
 * pour que chaque écran se recharge sur les données de l'org active.
 */
export function useDemoToggle() {
    const queryClient = useQueryClient();

    // L'org effective a changé côté serveur : on pousse le statut frais (bandeau/checklist
    // réagissent aussitôt) puis on invalide TOUTES les queries actives pour qu'elles se
    // rechargent sur la nouvelle org (tableau de bord, trésorerie, listes...).
    const refreshAll = (status: OnboardingStatus) => {
        queryClient.setQueryData(onboardingKeys.status(), status);
        queryClient.invalidateQueries();
    };

    const enter = useMutationWithToast<OnboardingStatus, Error, void>({
        mutationFn: () => onboardingApi.seedDemo(),
        operationName: 'Chargement de l\'exemple',
        onSuccess: refreshAll,
    });

    const reset = useMutationWithToast<OnboardingStatus, Error, void>({
        mutationFn: () => onboardingApi.reset(),
        operationName: 'Retour à votre espace',
        onSuccess: refreshAll,
    });

    return { enter, reset };
}
